import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

const NAV_DRAWER_OPEN_Z_INDEX = '10000';

class NavigationBarComponent extends Component {
  /** @type {HTMLElement|null} */
  #activeOverlay = null;

  /** @type {HTMLElement|null} */
  #activeNavItem = null;

  /** @type {number|null} */
  #drawerCloseTimeout = null;

  /** @type {MutationObserver|null} */
  #drawerObserver = null;

  /** @type {boolean} */
  #isDrawerOpen = false;

  /** @type {boolean} */
  #isDesktopScrollLocked = false;

  /** @type {string} */
  #previousBodyOverflow = '';

  /** @type {number|null} */
  #overlayHideTimeout = null;

  /** @type {number|null} */
  #scrollUnlockTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    this.#mountLiquidNavItems();
    this.#mountLiquidTemplateContent(document);
    this.#initDesktopOverlay();
    this.#initMobileDrawer();
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#drawerCloseTimeout) clearTimeout(this.#drawerCloseTimeout);
    if (this.#overlayHideTimeout) clearTimeout(this.#overlayHideTimeout);
    if (this.#scrollUnlockTimeout) clearTimeout(this.#scrollUnlockTimeout);
    this.#drawerObserver?.disconnect();
    this.#unlockDesktopScroll();
    this.style.removeProperty('z-index');
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getNavConfigs() {
    return Array.from(this.querySelectorAll('[data-nav-block-config]')).filter(
      (config) => config instanceof HTMLElement
    );
  }

  // Liquid owns the visible navigation markup. JS only mounts Liquid templates and wires behavior.
  #mountLiquidNavItems() {
    const desktopList = this.querySelector('[data-nav-desktop-list]');
    const mobileTablist = this.querySelector('[data-nav-tablist]');

    if (!(desktopList instanceof HTMLElement) || !(mobileTablist instanceof HTMLElement)) return;

    const desktopItems = Array.from(this.querySelectorAll('template[data-nav-desktop-template]'))
      .filter((template) => template instanceof HTMLTemplateElement)
      .map((template) => template.content.cloneNode(true));

    const mobileTabs = Array.from(this.querySelectorAll('template[data-nav-mobile-tab-template]'))
      .filter((template) => template instanceof HTMLTemplateElement)
      .map((template) => template.content.cloneNode(true));

    desktopList.replaceChildren(...desktopItems);
    mobileTablist.replaceChildren(...mobileTabs);
  }

  /**
   * @param {ParentNode} root
   */
  #mountLiquidTemplateContent(root) {
    this.#getNavConfigs().forEach((config) => {
      const blockId = config.dataset.blockId;
      const blockType = config.dataset.blockType;

      if (!blockId || !blockType) return;

      if (blockType === 'nav-all-brands') {
        this.#mountClonedLiquidTemplates(root, blockId, 'data-all-brands-desktop-items', this.#collectSiblingTemplates(config, 'template[data-brand-desktop-item]'));
        this.#mountClonedLiquidTemplates(root, blockId, 'data-all-brands-mobile-items', this.#collectSiblingTemplates(config, 'template[data-brand-mobile-item]'));
        return;
      }

      if (blockType === 'nav-benefit-v2') {
        const desktopBenefits = this.#collectSiblingTemplates(config, 'template[data-benefit-desktop-item]');
        const mobileBenefits = this.#collectSiblingTemplates(config, 'template[data-benefit-mobile-item]');
        this.#mountClonedLiquidTemplates(root, blockId, 'data-benefit-v2-desktop-items', desktopBenefits);
        this.#mountMobileBenefitItems(root, blockId, mobileBenefits);
        return;
      }

      if (blockType === 'nav-account') {
        this.#mountAccountMenu(root, config, blockId);
      }
    });
  }

  /**
   * @param {HTMLElement} config
   * @param {string} selector
   * @returns {HTMLTemplateElement[]}
   */
  #collectSiblingTemplates(config, selector) {
    const templates = [];
    let sibling = config.nextElementSibling;

    while (sibling instanceof HTMLElement && !sibling.hasAttribute('data-nav-block-config')) {
      if (sibling.matches(selector) && sibling instanceof HTMLTemplateElement) {
        templates.push(sibling);
      }

      sibling = sibling.nextElementSibling;
    }

    return templates;
  }

  /**
   * @param {ParentNode} root
   * @param {string} blockId
   * @param {string} attribute
   * @param {HTMLTemplateElement[]} templates
   */
  #mountClonedLiquidTemplates(root, blockId, attribute, templates) {
    const container = root.querySelector(`[${attribute}="${CSS.escape(blockId)}"]`);
    if (!(container instanceof HTMLElement)) return;

    container.replaceChildren(...templates.map((template) => template.content.cloneNode(true)));
  }

  /**
   * @param {ParentNode} root
   * @param {string} blockId
   * @param {HTMLTemplateElement[]} templates
   */
  #mountMobileBenefitItems(root, blockId, templates) {
    const container = root.querySelector(`[data-benefit-v2-mobile-items="${CSS.escape(blockId)}"]`);
    const list = root.querySelector(`[data-benefit-v2-mobile-list="${CSS.escape(blockId)}"]`);

    if (!(container instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    list.replaceChildren(...templates.map((template) => template.content.cloneNode(true)));
    container.hidden = templates.length === 0;
  }

  /**
   * @param {ParentNode} root
   * @param {HTMLElement} config
   * @param {string} blockId
   */
  #mountAccountMenu(root, config, blockId) {
    const itemsContainer = root.querySelector(`[data-account-mobile-items="${CSS.escape(blockId)}"]`);
    const utilityContainer = root.querySelector(`[data-account-mobile-utility="${CSS.escape(blockId)}"]`);
    const bottomContainer = root.querySelector(`[data-account-mobile-bottom="${CSS.escape(blockId)}"]`);

    if (!(itemsContainer instanceof HTMLElement) || !(utilityContainer instanceof HTMLElement)) return;

    const signedIn = bottomContainer instanceof HTMLElement && bottomContainer.dataset.customerSignedIn === 'true';
    const languageTarget = itemsContainer.dataset.languageTarget || '';
    const childItems = this.#collectAccountChildren(config);
    // Split after the LAST menu trigger so multiple menu items (e.g. several
    // "Shop by Category" entries) stay together in the top list instead of
    // spilling into the pinned-bottom utility block.
    let menuIndex = -1;
    childItems.forEach((item, index) => {
      if (item.action === 'menu') menuIndex = index;
    });
    const mainItems = menuIndex >= 0 ? childItems.slice(0, menuIndex + 1) : childItems;
    const utilityItems = menuIndex >= 0 ? childItems.slice(menuIndex + 1) : [];
    const signOutItem = childItems.find((item) => item.action === 'sign_out');

    itemsContainer.replaceChildren(
      ...mainItems
        .filter((item) => this.#shouldShowAccountItem(item, signedIn))
        .map((item) => this.#cloneAccountItem(item, languageTarget))
        .filter((item) => item instanceof Node)
    );

    const utilityNodes = this.#getVisibleAccountUtilityNodes(utilityItems, signedIn, languageTarget);
    utilityContainer.replaceChildren(...utilityNodes);
    utilityContainer.hidden = utilityNodes.length === 0;

    const bottomDivider = root.querySelector('[data-account-bottom-divider]');
    const bottomRow = root.querySelector('[data-account-bottom-row]');
    const signOutContainer = root.querySelector('[data-account-sign-out]');

    if (bottomDivider instanceof HTMLElement) {
      bottomDivider.classList.toggle('tw:mb-3.5', utilityNodes.length === 0);
      bottomDivider.classList.toggle('tw:my-3.5', utilityNodes.length > 0);
    }

    if (bottomRow instanceof HTMLElement) {
      bottomRow.hidden = !signedIn;
    }

    if (signOutContainer instanceof HTMLElement && signOutItem) {
      signOutContainer.replaceChildren(this.#cloneAccountItem(signOutItem, languageTarget));
    }
  }

  /**
   * @param {HTMLElement} config
   * @returns {Array<{ type: 'link'|'divider', action: string, template: HTMLTemplateElement }>}
   */
  #collectAccountChildren(config) {
    const items = [];
    let sibling = config.nextElementSibling;

    while (sibling instanceof HTMLElement && !sibling.hasAttribute('data-nav-block-config')) {
      if (sibling.hasAttribute('data-account-link-config')) {
        const template = sibling.nextElementSibling;
        if (template instanceof HTMLTemplateElement && template.hasAttribute('data-account-link-item')) {
          items.push({ type: 'link', action: sibling.dataset.action || 'link', template });
        }
      } else if (sibling.hasAttribute('data-account-divider-config')) {
        const template = sibling.nextElementSibling;
        if (template instanceof HTMLTemplateElement && template.hasAttribute('data-account-divider-item')) {
          items.push({ type: 'divider', action: 'divider', template });
        }
      }

      sibling = sibling.nextElementSibling;
    }

    return items;
  }

  /**
   * @param {{ type: 'link'|'divider', action: string }} item
   * @param {boolean} signedIn
   * @returns {boolean}
   */
  #shouldShowAccountItem(item, signedIn) {
    if (item.type === 'divider') return true;
    if (item.action === 'sign_out') return false;
    if (item.action === 'sign_in' && signedIn) return false;
    return true;
  }

  /**
   * @param {Array<{ type: 'link'|'divider', action: string, template: HTMLTemplateElement }>} items
   * @param {boolean} signedIn
   * @param {string} languageTarget
   * @returns {Node[]}
   */
  #getVisibleAccountUtilityNodes(items, signedIn, languageTarget) {
    const nodes = [];
    let pendingDivider = null;

    items.forEach((item) => {
      if (item.type === 'divider') {
        pendingDivider = item;
        return;
      }

      if (!this.#shouldShowAccountItem(item, signedIn)) return;

      if (pendingDivider) {
        nodes.push(this.#cloneAccountItem(pendingDivider, languageTarget));
        pendingDivider = null;
      }

      nodes.push(this.#cloneAccountItem(item, languageTarget));
    });

    return nodes.filter((node) => node instanceof Node);
  }

  /**
   * @param {{ template: HTMLTemplateElement }} item
   * @param {string} languageTarget
   * @returns {Node}
   */
  #cloneAccountItem(item, languageTarget) {
    const fragment = item.template.content.cloneNode(true);

    if (fragment instanceof DocumentFragment && languageTarget) {
      fragment.querySelectorAll('[data-account-language-link]').forEach((button) => {
        if (!(button instanceof HTMLElement)) return;
        button.setAttribute('data-nav-mobile-template-target', languageTarget);
        button.removeAttribute('data-account-language-link');
      });
    }

    return fragment;
  }

  #initDesktopOverlay() {
    const navItems = this.querySelectorAll('[data-nav-item]');
    const triggers = this.querySelectorAll('[data-mega-menu-target]');
    const overlays = document.querySelectorAll('[data-nav-overlay]');

    navItems.forEach((navItem) => {
      if (!(navItem instanceof HTMLElement)) return;
      if (navItem.hasAttribute('data-mega-menu-target')) return;

      navItem.addEventListener('mouseenter', () => {
        this.#cancelHideOverlay();
        this.#hideActiveOverlay();
        this.#setDesktopBorder(true);
      });
    });

    triggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;

      trigger.addEventListener('mouseenter', () => {
        this.#cancelHideOverlay();
        this.#showOverlay(trigger);
      });
    });

    const nav = this.querySelector('nav');
    if (nav instanceof HTMLElement) {
      nav.addEventListener('mouseleave', () => this.#scheduleHideOverlay());
      nav.addEventListener('mouseenter', this.#lockDesktopScroll);
      nav.addEventListener('mouseleave', this.#scheduleDesktopScrollUnlock);
    }

    overlays.forEach((overlay) => {
      if (!(overlay instanceof HTMLElement)) return;
      overlay.addEventListener('mouseenter', () => {
        this.#cancelHideOverlay();
        this.#lockDesktopScroll();
      });
      overlay.addEventListener('mouseleave', () => {
        this.#scheduleHideOverlay();
        this.#scheduleDesktopScrollUnlock();
      });
    });
  }

  #scheduleHideOverlay() {
    this.#overlayHideTimeout = window.setTimeout(() => {
      this.#overlayHideTimeout = null;
      this.#hideActiveOverlay();
    }, 100);
  }

  #cancelHideOverlay() {
    if (this.#overlayHideTimeout) {
      clearTimeout(this.#overlayHideTimeout);
      this.#overlayHideTimeout = null;
    }
  }

  #lockDesktopScroll = () => {
    if (window.innerWidth < 1024) return;

    if (this.#scrollUnlockTimeout) {
      clearTimeout(this.#scrollUnlockTimeout);
      this.#scrollUnlockTimeout = null;
    }

    if (this.#isDesktopScrollLocked || this.#isDrawerOpen) return;

    this.#previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.#isDesktopScrollLocked = true;
  };

  #scheduleDesktopScrollUnlock = () => {
    if (this.#scrollUnlockTimeout) clearTimeout(this.#scrollUnlockTimeout);

    this.#scrollUnlockTimeout = window.setTimeout(() => {
      this.#scrollUnlockTimeout = null;
      this.#unlockDesktopScroll();
    }, 100);
  };

  #unlockDesktopScroll = () => {
    if (!this.#isDesktopScrollLocked) return;

    if (!this.#isDrawerOpen) {
      document.body.style.overflow = this.#previousBodyOverflow;
    }

    this.#previousBodyOverflow = '';
    this.#isDesktopScrollLocked = false;
  };

  /**
   * @param {HTMLElement} trigger
   */
  #showOverlay(trigger) {
    const targetId = trigger.getAttribute('data-mega-menu-target');
    if (!targetId) return;

    const overlay = document.querySelector(`[data-nav-overlay][data-mega-menu-id="${CSS.escape(targetId)}"]`);
    if (!(overlay instanceof HTMLElement)) return;

    if (this.#activeOverlay && this.#activeOverlay !== overlay) {
      this.#hideActiveOverlay();
    }

    const nav = this.querySelector('nav');
    if (nav instanceof HTMLElement) {
      const rect = nav.getBoundingClientRect();
      overlay.style.top = `${rect.bottom}px`;
    }

    overlay.classList.remove('tw:invisible', 'tw:-translate-y-2', 'tw:opacity-0', 'tw:pointer-events-none');
    overlay.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    this.#setDesktopBorder(trigger.dataset.showNavBorder !== 'false');

    this.#activeOverlay = overlay;
    this.#activeNavItem = trigger;
  }

  /**
   * @param {boolean} visible
   */
  #setDesktopBorder(visible) {
    this.querySelector('nav')?.classList.toggle('tw:border-b', visible);
  }

  #hideActiveOverlay() {
    if (this.#activeOverlay) {
      this.#activeOverlay.classList.add('tw:invisible', 'tw:-translate-y-2', 'tw:opacity-0', 'tw:pointer-events-none');
      this.#activeOverlay.setAttribute('aria-hidden', 'true');
      this.#activeOverlay = null;
    }

    if (this.#activeNavItem) {
      this.#activeNavItem.setAttribute('aria-expanded', 'false');
      this.#activeNavItem = null;
    }

    this.#setDesktopBorder(true);
  }

  #initMobileDrawer() {
    document.querySelectorAll('[data-nav-drawer-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.#openDrawer());
    });

    const details = document.querySelector('#Details-menu-drawer-container');

    if (details instanceof HTMLDetailsElement) {
      this.#drawerObserver = new MutationObserver(() => {
        if (!details.open || window.innerWidth >= 1024) return;

        this.#resetNativeDrawer();
        this.#openDrawer();
        requestAnimationFrame(this.#resetNativeDrawer);
      });

      this.#drawerObserver.observe(details, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    this.querySelector('[data-nav-drawer-close]')?.addEventListener('click', () => this.#closeDrawer());

    this.querySelector('[data-nav-drawer-panel]')?.addEventListener('click', this.#handleMobilePanelClick);

    this.querySelectorAll('[data-nav-tab]').forEach((tab) => {
      if (tab instanceof HTMLElement) {
        tab.addEventListener('click', () => this.#switchTab(tab, 'right'));
      }
    });
  }

  #openDrawer() {
    const drawer = this.querySelector('[data-nav-drawer]');
    if (!(drawer instanceof HTMLElement)) return;

    if (this.#drawerCloseTimeout) {
      clearTimeout(this.#drawerCloseTimeout);
      this.#drawerCloseTimeout = null;
    }

    const selectedTab = this.#getInitialTab();
    if (selectedTab) this.#switchTab(selectedTab);

    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.remove('tw:invisible', 'tw:-translate-x-full', 'tw:opacity-0', 'tw:pointer-events-none');
    this.style.zIndex = NAV_DRAWER_OPEN_Z_INDEX;
    document.body.style.overflow = 'hidden';
    this.#isDrawerOpen = true;

    requestAnimationFrame(() => trapFocus(drawer));
  }

  #closeDrawer() {
    const drawer = this.querySelector('[data-nav-drawer]');
    if (!(drawer instanceof HTMLElement)) return;

    this.#resetNativeDrawer();
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.add('tw:invisible', 'tw:-translate-x-full', 'tw:opacity-0', 'tw:pointer-events-none');
    this.style.removeProperty('z-index');
    document.body.style.overflow = '';
    this.#isDrawerOpen = false;
    removeTrapFocus();

    this.#drawerCloseTimeout = window.setTimeout(() => {
      this.#drawerCloseTimeout = null;
    }, 300);
  }

  #resetNativeDrawer = () => {
    const details = document.querySelector('#Details-menu-drawer-container');
    if (!(details instanceof HTMLDetailsElement)) return;

    details.open = false;
    details.removeAttribute('open');
    details.classList.remove('menu-open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    details.querySelector('.menu-drawer')?.classList.remove('menu-drawer--has-submenu-opened');

    details.querySelectorAll('details[open]').forEach((openDetails) => {
      if (!(openDetails instanceof HTMLDetailsElement)) return;
      openDetails.open = false;
      openDetails.removeAttribute('open');
      openDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  };

  /**
   * @returns {HTMLElement|null}
   */
  #getInitialTab() {
    // A tab the app CSS hides (@showapp / @hideapp on the block label, see
    // snippets/mobile-app-webview.liquid) must not become the default panel.
    const tabs = Array.from(this.querySelectorAll('[data-nav-tab]')).filter(
      (tab) => tab instanceof HTMLElement && getComputedStyle(tab).display !== 'none'
    );

    const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (selected instanceof HTMLElement) return selected;

    const tabWithTemplate = tabs.find((tab) => {
      if (!(tab instanceof HTMLElement)) return false;
      const megaMenuId = tab.getAttribute('data-nav-tab-mega-menu-id');
      return megaMenuId ? Boolean(document.querySelector(`template[data-mega-menu-mobile="${CSS.escape(megaMenuId)}"]`)) : false;
    });

    return tabWithTemplate instanceof HTMLElement ? tabWithTemplate : /** @type {HTMLElement|null} */ (tabs[0] ?? null);
  }

  /**
   * @param {HTMLElement} activeTab
   * @param {'left'|'right'} [slideDirection]
   */
  #switchTab(activeTab, slideDirection) {
    this.#showMobileTablist();

    const tabs = this.querySelectorAll('[data-nav-tab]');

    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      tab.setAttribute('aria-selected', 'false');
      tab.style.borderBottomColor = 'transparent';
      tab.style.fontWeight = '300';
    });

    activeTab.setAttribute('aria-selected', 'true');
    activeTab.style.borderBottomColor = 'var(--navigation-active-tab-border-color, #000000)';
    activeTab.style.fontWeight = '300';

    const panel = this.querySelector('[data-nav-drawer-panel]');
    if (!(panel instanceof HTMLElement)) return;

    panel.replaceChildren();

    const megaMenuId = activeTab.getAttribute('data-nav-tab-mega-menu-id');
    if (megaMenuId) {
      if (this.#renderMobileTemplate(megaMenuId, slideDirection)) return;
    }
  }

  /**
   * @param {Event} event
   */
  #handleMobilePanelClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const summaryEl = event.target.closest('summary');
    if (summaryEl instanceof HTMLElement) {
      const detailsEl = summaryEl.closest('details');
      if (detailsEl instanceof HTMLDetailsElement) {
        event.preventDefault();
        this.#toggleAccordion(detailsEl);
        return;
      }
    }

    const backButton = event.target.closest('[data-nav-mobile-back]');
    if (backButton instanceof HTMLElement) {
      event.preventDefault();
      const activeTab = this.querySelector('[data-nav-tab][aria-selected="true"]');
      this.#showMobileTablist();
      if (activeTab instanceof HTMLElement) this.#switchTab(activeTab, 'left');
      return;
    }

    const categoryTrigger = event.target.closest('[data-account-category-link]');
    if (categoryTrigger instanceof HTMLElement) {
      const categoryConfig = this.#getNavConfigs().find(
        (config) => config.dataset.blockType === 'nav-category' && config.dataset.blockId
      );
      const categoryTemplateId = categoryConfig?.dataset.blockId;

      if (categoryTemplateId) {
        event.preventDefault();
        this.#hideMobileTablist();
        this.#renderMobileTemplate(categoryTemplateId, 'right', true);
        return;
      }
    }

    const templateTrigger = event.target.closest('[data-nav-mobile-template-target]');
    if (!(templateTrigger instanceof HTMLElement)) return;

    const templateId = templateTrigger.getAttribute('data-nav-mobile-template-target');
    if (!templateId) return;

    event.preventDefault();
    this.#hideMobileTablist();
    this.#renderMobileTemplate(templateId, 'right', true);
  };

  /**
   * @param {string} templateId
   * @param {'left'|'right'} [slideDirection]
   * @param {boolean} [nested] - True when the panel was pushed from another
   *   panel, which is the only case where a back control makes sense.
   * @returns {boolean}
   */
  #renderMobileTemplate(templateId, slideDirection, nested = false) {
    const panel = this.querySelector('[data-nav-drawer-panel]');
    if (!(panel instanceof HTMLElement)) return false;

    const template = document.querySelector(`template[data-mega-menu-mobile="${CSS.escape(templateId)}"]`);
    if (!(template instanceof HTMLTemplateElement)) return false;

    panel.replaceChildren();
    panel.appendChild(template.content.cloneNode(true));
    panel.scrollTop = 0;
    this.#resolveInlineTemplates(panel);
    this.#mountLiquidTemplateContent(panel);

    // A panel opened from a tab is already at the top level of the drawer, so
    // there is nowhere for "Back to main menu" to go. The markup carries the
    // control for the nested case, so drop it here rather than leave a button
    // that looks active and does nothing. Removing beats hiding: a hidden one
    // is still reachable by keyboard.
    if (!nested) {
      panel.querySelectorAll('[data-nav-mobile-back]').forEach((button) => button.remove());
    }

    if (slideDirection) {
      const offset = slideDirection === 'right' ? '12px' : '-12px';
      panel.style.opacity = '0';
      panel.style.transform = `translateX(${offset})`;
      requestAnimationFrame(() => {
        panel.style.transition = 'opacity 200ms ease, transform 200ms ease';
        panel.style.opacity = '1';
        panel.style.transform = 'translateX(0)';
        panel.addEventListener('transitionend', () => { panel.style.cssText = ''; }, { once: true });
      });
    }

    return true;
  }

  /**
   * @param {HTMLDetailsElement} detailsEl
   */
  #toggleAccordion(detailsEl) {
    const body = detailsEl.querySelector('[data-accordion-body]');
    if (!(body instanceof HTMLElement)) {
      if (detailsEl.open) detailsEl.removeAttribute('open');
      else detailsEl.setAttribute('open', '');
      return;
    }

    if (detailsEl.open) {
      const height = body.scrollHeight;
      body.style.height = `${height}px`;
      body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        body.style.transition = 'height 250ms ease, opacity 250ms ease';
        body.style.height = '0';
        body.style.opacity = '0';
        body.addEventListener('transitionend', () => {
          detailsEl.removeAttribute('open');
          body.style.cssText = '';
        }, { once: true });
      });
    } else {
      detailsEl.setAttribute('open', '');
      const height = body.scrollHeight;
      body.style.height = '0';
      body.style.overflow = 'hidden';
      body.style.opacity = '0';
      requestAnimationFrame(() => {
        body.style.transition = 'height 250ms ease, opacity 250ms ease';
        body.style.height = `${height}px`;
        body.style.opacity = '1';
        body.addEventListener('transitionend', () => { body.style.cssText = ''; }, { once: true });
      });
    }
  }

  /**
   * Replaces [data-mega-menu-inline] placeholders with the content of the
   * referenced mobile template, allowing one template to embed another inline.
   * @param {HTMLElement} container
   */
  #resolveInlineTemplates(container) {
    container.querySelectorAll('[data-mega-menu-inline]').forEach((placeholder) => {
      if (!(placeholder instanceof HTMLElement)) return;
      const inlineId = placeholder.getAttribute('data-mega-menu-inline');
      if (!inlineId) return;
      const inlineTemplate = document.querySelector(`template[data-mega-menu-mobile="${CSS.escape(inlineId)}"]`);
      if (!(inlineTemplate instanceof HTMLTemplateElement)) return;
      placeholder.appendChild(inlineTemplate.content.cloneNode(true));
    });
  }

  #hideMobileTablist() {
    this.querySelector('[data-nav-tablist]')?.classList.add('tw:hidden');
  }

  #showMobileTablist() {
    this.querySelector('[data-nav-tablist]')?.classList.remove('tw:hidden');
  }

  /**
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!this.#activeOverlay) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target) || this.#activeOverlay.contains(event.target)) return;

    this.#hideActiveOverlay();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#isDrawerOpen) {
      this.#closeDrawer();
      return;
    }

    this.#hideActiveOverlay();
  };
}

if (!customElements.get('navigation-bar-component')) {
  customElements.define('navigation-bar-component', NavigationBarComponent);
}
